import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  getAllModuleDefinitions,
  getRoles,
  getUsers,
  getRoleModuleAccess,
  updateRoleModuleAccess,
  getUserModuleOverrides,
  updateUserModuleOverrides,
} from '../api';
import { Settings, Shield, Eye, Save, Users, GripVertical } from 'lucide-react';

function ModuleManagement() {
  const { hasPermission, user, refreshModules } = useAuth();
  const [activeTab, setActiveTab] = useState('role');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Shared data
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);

  // Role tab state
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [roleModuleAccess, setRoleModuleAccess] = useState([]);
  const [loadingRoleAccess, setLoadingRoleAccess] = useState(false);

  // User tab state
  const [selectedUserId, setSelectedUserId] = useState('');
  const [userModuleOverrides, setUserModuleOverrides] = useState([]);
  const [loadingUserOverrides, setLoadingUserOverrides] = useState(false);

  // Drag state
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const [modulesRes, rolesRes, usersRes] = await Promise.all([
        getAllModuleDefinitions(),
        getRoles(),
        getUsers(),
      ]);
      setModules(modulesRes.data);
      setRoles(rolesRes.data);
      setUsers(usersRes.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load initial data.' });
    } finally {
      setLoading(false);
    }
  };

  const clearMessage = () => {
    setMessage({ type: '', text: '' });
  };

  // Load role module access when a role is selected
  const loadRoleModuleAccess = useCallback(async (roleId) => {
    if (!roleId) {
      setRoleModuleAccess([]);
      return;
    }
    setLoadingRoleAccess(true);
    clearMessage();
    try {
      const res = await getRoleModuleAccess(roleId);
      const modulesList = res.data.modules || [];
      const merged = modulesList.map((mod) => ({
        module_key: mod.module_key,
        module_name: mod.module_name,
        is_core: mod.is_core,
        default_icon: mod.default_icon,
        is_visible: mod.is_visible,
        sort_order: mod.sort_order,
      }));
      merged.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setRoleModuleAccess(merged);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load role module access.' });
    } finally {
      setLoadingRoleAccess(false);
    }
  }, [modules]);

  // Load user module overrides when a user is selected
  const loadUserModuleOverrides = useCallback(async (userId) => {
    if (!userId) {
      setUserModuleOverrides([]);
      return;
    }
    setLoadingUserOverrides(true);
    clearMessage();
    try {
      const res = await getUserModuleOverrides(userId);
      const modulesList = res.data.modules || [];
      const merged = modulesList.map((mod) => ({
        module_key: mod.module_key,
        module_name: mod.module_name,
        is_core: mod.is_core,
        default_icon: mod.default_icon,
        is_visible: mod.override_is_visible !== undefined ? mod.override_is_visible : null,
        sort_order: mod.override_sort_order !== undefined ? mod.override_sort_order : null,
      }));
      merged.sort((a, b) => {
        const aSort = a.sort_order !== null ? a.sort_order : 0;
        const bSort = b.sort_order !== null ? b.sort_order : 0;
        return aSort - bSort;
      });
      setUserModuleOverrides(merged);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load user module overrides.' });
    } finally {
      setLoadingUserOverrides(false);
    }
  }, [modules]);

  useEffect(() => {
    if (selectedRoleId && modules.length > 0) {
      loadRoleModuleAccess(selectedRoleId);
    }
  }, [selectedRoleId, modules, loadRoleModuleAccess]);

  useEffect(() => {
    if (selectedUserId && modules.length > 0) {
      loadUserModuleOverrides(selectedUserId);
    }
  }, [selectedUserId, modules, loadUserModuleOverrides]);

  // Drag and drop handlers
  const handleDragStart = (index) => {
    dragItem.current = index;
    setDragIndex(index);
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = (listType) => {
    const from = dragItem.current;
    const to = dragOverItem.current;
    dragItem.current = null;
    dragOverItem.current = null;
    setDragIndex(null);

    if (from === null || to === null || from === to) return;

    if (listType === 'role') {
      setRoleModuleAccess((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(from, 1);
        updated.splice(to, 0, moved);
        return updated.map((item, i) => ({ ...item, sort_order: i }));
      });
    } else {
      setUserModuleOverrides((prev) => {
        const updated = [...prev];
        const [moved] = updated.splice(from, 1);
        updated.splice(to, 0, moved);
        return updated.map((item, i) => ({ ...item, sort_order: i }));
      });
    }
  };

  // Role access handlers
  const handleRoleVisibilityToggle = (moduleKey) => {
    setRoleModuleAccess((prev) =>
      prev.map((item) =>
        item.module_key === moduleKey && !item.is_core
          ? { ...item, is_visible: item.is_visible ? 0 : 1 }
          : item
      )
    );
  };

  const handleSaveRoleAccess = async () => {
    if (!selectedRoleId) return;
    setSaving(true);
    clearMessage();
    try {
      const payload = roleModuleAccess.map((item) => ({
        module_key: item.module_key,
        is_visible: item.is_core ? 1 : item.is_visible,
        sort_order: item.sort_order,
      }));
      await updateRoleModuleAccess(selectedRoleId, payload);
      setMessage({ type: 'success', text: 'Role module access saved successfully.' });
      // MM-001: only refresh the live nav when the edited role is the current
      // user's OWN role. Editing any other role must never touch this session.
      if (user && String(selectedRoleId) === String(user.role_id)) {
        refreshModules();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save role module access.' });
    } finally {
      setSaving(false);
    }
  };

  // User override handlers
  const handleUserVisibilityChange = (moduleKey, value) => {
    setUserModuleOverrides((prev) =>
      prev.map((item) =>
        item.module_key === moduleKey && !item.is_core
          ? { ...item, is_visible: value === 'inherit' ? null : parseInt(value, 10) }
          : item
      )
    );
  };

  const handleSaveUserOverrides = async () => {
    if (!selectedUserId) return;
    setSaving(true);
    clearMessage();
    try {
      const payload = userModuleOverrides.map((item) => ({
        module_key: item.module_key,
        is_visible: item.is_core ? 1 : item.is_visible,
        sort_order: item.sort_order,
      }));
      await updateUserModuleOverrides(selectedUserId, payload);
      setMessage({ type: 'success', text: 'User module overrides saved successfully.' });
      // MM-001: only refresh the live nav when editing the current user's OWN
      // overrides. Editing any other user must never touch this session.
      if (user && String(selectedUserId) === String(user.id)) {
        refreshModules();
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to save user module overrides.' });
    } finally {
      setSaving(false);
    }
  };

  // Permission check
  if (!hasPermission('manage_settings')) {
    return (
      <div style={{ padding: '24px' }}>
        <div className="alert alert-error">You do not have permission to manage modules.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const dragRowStyle = (index) => ({
    opacity: dragIndex === index ? 0.4 : 1,
    cursor: 'default',
  });

  return (
    <div className="module-management-page">
      {/* Page Header */}
      <div className="page-header" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <Settings size={24} style={{ color: 'var(--accent-primary)' }} />
          <h1 className="page-title" style={{ margin: 0 }}>Module Management</h1>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', margin: 0 }}>
          Control which modules are visible for each role, and set per-user overrides when needed. Drag rows to reorder.
        </p>
      </div>

      {/* Messages */}
      {message.text && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          {message.text}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'role' ? 'active' : ''}`}
          onClick={() => { setActiveTab('role'); clearMessage(); }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Shield size={16} /> By Role
          </span>
        </button>
        <button
          className={`tab ${activeTab === 'user' ? 'active' : ''}`}
          onClick={() => { setActiveTab('user'); clearMessage(); }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Users size={16} /> By User
          </span>
        </button>
      </div>

      {/* By Role Tab */}
      {activeTab === 'role' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Role Module Visibility</h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Select Role</label>
              <select
                className="form-select"
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                style={{ maxWidth: '320px' }}
              >
                <option value="">-- Choose a role --</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>{role.name}</option>
                ))}
              </select>
            </div>

            {loadingRoleAccess && (
              <div className="loading-container" style={{ padding: '40px 0' }}>
                <div className="spinner"></div>
              </div>
            )}

            {selectedRoleId && !loadingRoleAccess && roleModuleAccess.length > 0 && (
              <>
                <div className="table-container" style={{ marginTop: '16px' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}></th>
                        <th>Module</th>
                        <th style={{ width: '120px', textAlign: 'center' }}>Visible</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roleModuleAccess.map((item, index) => (
                        <tr
                          key={item.module_key}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragEnter={() => handleDragEnter(index)}
                          onDragEnd={() => handleDragEnd('role')}
                          onDragOver={(e) => e.preventDefault()}
                          style={dragRowStyle(index)}
                        >
                          <td style={{ width: '40px', textAlign: 'center', cursor: 'grab' }}>
                            <GripVertical size={16} style={{ color: 'var(--text-tertiary)' }} />
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontWeight: 500 }}>{item.module_name}</span>
                              {item.is_core ? (
                                <span className="badge badge-primary" style={{ fontSize: '11px' }}>Core</span>
                              ) : null}
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {item.is_core ? (
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: 'var(--accent-success)',
                                fontSize: '13px',
                              }}>
                                <Eye size={14} /> Always On
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => handleRoleVisibilityToggle(item.module_key)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '48px',
                                  height: '26px',
                                  borderRadius: '13px',
                                  border: 'none',
                                  cursor: 'pointer',
                                  backgroundColor: item.is_visible ? 'var(--accent-success)' : 'var(--bg-tertiary)',
                                  transition: 'background-color 0.2s',
                                  position: 'relative',
                                }}
                                title={item.is_visible ? 'Visible - Click to hide' : 'Hidden - Click to show'}
                              >
                                <span style={{
                                  display: 'block',
                                  width: '20px',
                                  height: '20px',
                                  borderRadius: '50%',
                                  backgroundColor: 'white',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                  transition: 'transform 0.2s',
                                  transform: item.is_visible ? 'translateX(10px)' : 'translateX(-10px)',
                                }} />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  padding: '16px 0 0',
                  borderTop: '1px solid var(--border-color)',
                  marginTop: '16px',
                }}>
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveRoleAccess}
                    disabled={saving}
                  >
                    <Save size={16} />
                    {saving ? 'Saving...' : 'Save Role Access'}
                  </button>
                </div>
              </>
            )}

            {selectedRoleId && !loadingRoleAccess && roleModuleAccess.length === 0 && (
              <p style={{ color: 'var(--text-secondary)', padding: '20px 0', textAlign: 'center' }}>
                No modules found. Ensure module definitions have been configured.
              </p>
            )}

            {!selectedRoleId && (
              <p style={{ color: 'var(--text-secondary)', padding: '20px 0', textAlign: 'center' }}>
                Select a role above to configure its module visibility.
              </p>
            )}
          </div>
        </div>
      )}

      {/* By User Tab */}
      {activeTab === 'user' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">User Module Overrides</h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">Select User</label>
              <select
                className="form-select"
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                style={{ maxWidth: '320px' }}
              >
                <option value="">-- Choose a user --</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                ))}
              </select>
            </div>

            <div
              className="alert alert-warning"
              style={{ marginBottom: '16px', display: selectedUserId ? 'block' : 'none' }}
            >
              User overrides take precedence over role-level settings. Use "Inherit from Role" to defer to the role configuration. Drag rows to reorder.
            </div>

            {loadingUserOverrides && (
              <div className="loading-container" style={{ padding: '40px 0' }}>
                <div className="spinner"></div>
              </div>
            )}

            {selectedUserId && !loadingUserOverrides && userModuleOverrides.length > 0 && (
              <>
                <div className="table-container" style={{ marginTop: '8px' }}>
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}></th>
                        <th>Module</th>
                        <th style={{ width: '200px', textAlign: 'center' }}>Visibility Override</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userModuleOverrides.map((item, index) => (
                        <tr
                          key={item.module_key}
                          draggable
                          onDragStart={() => handleDragStart(index)}
                          onDragEnter={() => handleDragEnter(index)}
                          onDragEnd={() => handleDragEnd('user')}
                          onDragOver={(e) => e.preventDefault()}
                          style={dragRowStyle(index)}
                        >
                          <td style={{ width: '40px', textAlign: 'center', cursor: 'grab' }}>
                            <GripVertical size={16} style={{ color: 'var(--text-tertiary)' }} />
                          </td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <span style={{ fontWeight: 500 }}>{item.module_name}</span>
                              {item.is_core ? (
                                <span className="badge badge-primary" style={{ fontSize: '11px' }}>Core</span>
                              ) : null}
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {item.is_core ? (
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: 'var(--accent-success)',
                                fontSize: '13px',
                              }}>
                                <Eye size={14} /> Always On
                              </span>
                            ) : (
                              <select
                                className="form-select"
                                value={item.is_visible === null ? 'inherit' : String(item.is_visible)}
                                onChange={(e) => handleUserVisibilityChange(item.module_key, e.target.value)}
                                style={{
                                  width: '180px',
                                  padding: '6px 10px',
                                  fontSize: '13px',
                                  backgroundColor: item.is_visible === null
                                    ? 'var(--bg-secondary)'
                                    : item.is_visible === 1
                                      ? 'rgba(34, 197, 94, 0.08)'
                                      : 'rgba(239, 68, 68, 0.08)',
                                  borderColor: item.is_visible === null
                                    ? 'var(--border-color)'
                                    : item.is_visible === 1
                                      ? 'rgba(34, 197, 94, 0.3)'
                                      : 'rgba(239, 68, 68, 0.3)',
                                }}
                              >
                                <option value="inherit">Inherit from Role</option>
                                <option value="1">Force Visible</option>
                                <option value="0">Force Hidden</option>
                              </select>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  padding: '16px 0 0',
                  borderTop: '1px solid var(--border-color)',
                  marginTop: '16px',
                }}>
                  <button
                    className="btn btn-primary"
                    onClick={handleSaveUserOverrides}
                    disabled={saving}
                  >
                    <Save size={16} />
                    {saving ? 'Saving...' : 'Save User Overrides'}
                  </button>
                </div>
              </>
            )}

            {selectedUserId && !loadingUserOverrides && userModuleOverrides.length === 0 && (
              <p style={{ color: 'var(--text-secondary)', padding: '20px 0', textAlign: 'center' }}>
                No modules found. Ensure module definitions have been configured.
              </p>
            )}

            {!selectedUserId && (
              <p style={{ color: 'var(--text-secondary)', padding: '20px 0', textAlign: 'center' }}>
                Select a user above to configure their module overrides.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ModuleManagement;
